import { useRouter } from 'next/router'
import { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/forms/[handler]'
import { hasNoLetters } from '../../const/letterHelper'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import { TextInput } from '../form/TextInput'
import { InfoModal } from './InfoModal'
import { MessageInfoSchema } from './MessageInfoSchema'
import style from './SuggestionModal.module.sass'

type NullableValues<R extends Record<string, unknown>> = {
	[K in keyof R]: R[K] | null
}
type NullableMessageInfoSchema = NullableValues<MessageInfoSchema>

type ContactFormMessageModalProps = {
	isModalOpen: boolean
	close: () => void
}

const api = createFetcher<API>('/api/forms/[handler]')

export const ContactFormMessageModal: FunctionComponent<ContactFormMessageModalProps> = ({
	isModalOpen = false,
	close,
}) => {
	const translate = useTranslate()
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
	const [isAlertVisible, setIsAlertVisible] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [email, setEmail] = useState<string | null>(null)
	const [emaiAlertVisible, setEmaiAlertVisible] = useState(false)

	const router = useRouter()

	const closeModal = useCallback(() => {
		setMessage('')
		setEmail('')
		close()
		setIsAlertVisible(false)
		router.replace({ pathname: router.pathname }, undefined, {
			scroll: false,
		})
	}, [close, router])

	const submitMessage = async () => {
		if (message && message.length >= 10 && !hasNoLetters(message)) {
			setIsLoading(true)
			try {
				await api('createContactFormMessage', {
					email: String(email),
					message: String(message),
					url: router.asPath,
				})
				await api('sendMailReport', {
					email: String(email),
					message: String(message),
					url: router.asPath,
				})
			} catch (err) {
				console.log('error', err)
			}
			setIsConfirmModalOpen(true)
			setIsLoading(false)
			closeModal()
			return
		}
		setIsAlertVisible(true)
	}

	const validateEmail = (email: string) => {
		const regex = /\S+@\S+\.\S+/
		const text = regex.test(email)
		if (!text) {
			setEmaiAlertVisible(true)
		}
	}

	const onChange = useCallback((change: NullableMessageInfoSchema) => {
		console.log({ change })
	}, [])

	const schema = useMemo<NullableMessageInfoSchema>(() => {
		return {
			message,
			email,
		}
	}, [email, message])

	useEffect(() => {
		console.log('onChange', schema)
		onChange?.(schema)
	}, [schema, onChange])

	const messageInfo = useMemo(() => {
		return MessageInfoSchema.safeParse({ ...schema })
	}, [schema])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const checkValidity = useCallback(() => {
		email && validateEmail(String(email))
		if ((message && message.length <= 10) || hasNoLetters(message)) {
			setIsAlertVisible(true)
		}
	}, [email, message])

	return (
		<>
			{isModalOpen && (
				<InfoModal
					title={translate('ContactFormMessageModal.title')}
					lead={translate('ContactFormMessageModal.subtitle')}
					imageUrl="/images/pejsek.jpg"
					isOpen={isModalOpen}
					buttonLabel={isLoading ? <div className={style.loader} /> : translate('ContactFormMessageModal.button.send')}
					buttonType="submit"
					imageAlternativeText={translate('ContactFormMessageModal.alt.image')}
					onClick={messageInfo.success ? submitMessage : checkValidity}
					disabled={!message || message.length < 10 || !email}
					closeModal={closeModal}
					content={
						<div className={style.modalContent}>
							<div className={style.inputWrapper}>
								<textarea
									className={style.inputTextArea}
									placeholder={translate('ContactFormMessageModal.textarea.placeholder')}
									rows={6}
									value={message || undefined}
									onChange={(e) => setMessage(e?.target.value)}
									onFocus={() => setIsAlertVisible(false)}
								/>
								{isAlertVisible && (
									<span className={style.inputAlert}>
										{hasNoLetters(message) ? (
											<Translation tr="message.punctuation.alert" />
										) : (
											<Translation tr="ContactFormMessageModal.alert" />
										)}
									</span>
								)}
							</div>
							<div className={style.inputEmailWrapper}>
								<TextInput
									name="email"
									type="email"
									onChange={(e) => setEmail(e.target.value)}
									value={email ?? ''}
									placeholder={translate('email.placeholder')}
									onFocus={() => setEmaiAlertVisible(false)}
								/>

								{emaiAlertVisible && (
									<span className={style.inputAlert}>
										<Translation tr="ContactFormMessageModal.email.alert" />
									</span>
								)}
							</div>
						</div>
					}
				/>
			)}
			{isConfirmModalOpen && (
				<InfoModal
					title={translate('ContactFormMessageModal.confirm.modal.title')}
					lead={translate('ContactFormMessageModal.confirm.subtitle')}
					imageUrl="/images/pes_modal_2.jpg"
					isOpen={isConfirmModalOpen}
					buttonLabel={translate('ContactFormMessageModal.confirm.button.close')}
					buttonType="button"
					imageAlternativeText={translate('ContactFormMessageModal.confirm.alt.image')}
					onClick={() => setIsConfirmModalOpen(false)}
					closeModal={() => setIsConfirmModalOpen(false)}
				/>
			)}
		</>
	)
}
